import { useEffect, useRef, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type Palette = { bg: string; fg: string; muted: string; border: string };
type ThemeKey = 'lightGray' | 'dark' | 'white';

const PALETTES: Record<ThemeKey, Palette> = {
  lightGray: { bg: 'rgba(241,239,240,1)', fg: 'rgba(40,37,35,1)', muted: 'rgba(40,37,35,0.72)', border: 'rgba(40,37,35,0.16)' },
  dark: { bg: 'rgba(40,37,35,1)', fg: 'rgba(255,255,255,1)', muted: 'rgba(255,255,255,0.7)', border: 'rgba(255,255,255,0.18)' },
  white: { bg: 'rgba(255,255,255,1)', fg: 'rgba(40,37,35,1)', muted: 'rgba(40,37,35,0.72)', border: 'rgba(40,37,35,0.16)' },
};

const BG_DURATION = 1.05;
const TEXT_DELAY = 0.55;
const TEXT_DURATION = 0.2;

interface Props {
  children: ReactNode;
}

/**
 * Shared scoped theme environment for home sections 03–06.
 * Threshold-triggered (not scrubbed): crossing a threshold picks a target theme,
 * then one self-completing timeline animates to it. Text switches quickly once
 * the background has passed its midpoint so it never sits gray-on-gray.
 */
export default function ThemeChapter({ children }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const darkStart = root.querySelector<HTMLElement>('[data-chapter-dark]');
    const lightStart = root.querySelector<HTMLElement>('[data-chapter-light]');
    if (!darkStart || !lightStart) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const state: Palette = { ...PALETTES.lightGray };
    let current: ThemeKey | null = null;
    let tl: gsap.core.Timeline | null = null;

    const write = () => {
      root.style.setProperty('--chapter-bg', state.bg);
      root.style.setProperty('--chapter-fg', state.fg);
      root.style.setProperty('--chapter-muted', state.muted);
      root.style.setProperty('--chapter-border', state.border);
    };

    const goTo = (key: ThemeKey, instant: boolean) => {
      if (key === current) return;
      current = key;
      root.dataset.chapterTheme = key;
      tl?.kill();
      const target = PALETTES[key];
      if (instant || reduce) {
        Object.assign(state, target);
        write();
        return;
      }
      tl = gsap.timeline({ onUpdate: write, onComplete: write });
      tl.to(state, { bg: target.bg, duration: BG_DURATION, ease: 'power2.inOut' }, 0);
      tl.to(state, {
        fg: target.fg, muted: target.muted, border: target.border, duration: TEXT_DURATION, ease: 'power1.out',
      }, TEXT_DELAY);
    };

    let a: ScrollTrigger | null = null;
    let b: ScrollTrigger | null = null;
    let initialised = false;

    const evaluate = () => {
      if (!a || !b) return;
      const scroll = a.scroll();
      let key: ThemeKey = 'lightGray';
      if (scroll >= b.start) key = 'white';
      else if (scroll >= a.start) key = 'dark';
      goTo(key, !initialised);
      initialised = true;
    };

    const common = { start: 'top 60%', end: 'max', onToggle: evaluate, onRefresh: evaluate };
    a = ScrollTrigger.create({ trigger: darkStart, ...common });
    // Dark → Light must wait until the pinned review rail has fully released.
    b = ScrollTrigger.create({
      trigger: lightStart,
      ...common,
      refreshPriority: -10,
      start: () => {
        const rail = ScrollTrigger.getById('client-rail');
        const natural = lightStart.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.6;
        if (!rail) return natural;
        return Math.max(natural, rail.end + window.innerHeight * 0.4);
      },
    });
    evaluate();

    // Refresh only on a real, settled height change. Heights written by the refresh
    // itself (e.g. the review rail sizing its outer box) are recorded afterwards, so
    // they can't trigger a second refresh and a mid-scroll rail jump.
    let lastH = root.offsetHeight;
    let timer = 0;
    const onRefreshed = () => { lastH = root.offsetHeight; };
    ScrollTrigger.addEventListener('refresh', onRefreshed);
    const ro = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (Math.abs(root.offsetHeight - lastH) > 2) ScrollTrigger.refresh();
      }, 150);
    });
    ro.observe(root);
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);

    return () => {
      window.clearTimeout(timer);
      ScrollTrigger.removeEventListener('refresh', onRefreshed);
      ro.disconnect();
      window.removeEventListener('load', onLoad);
      tl?.kill();
      a?.kill();
      b?.kill();
    };
  }, []);

  return (
    <div ref={ref} className="theme-chapter">
      {children}
    </div>
  );
}